import type { PropertyValidationResult } from '../../../types/properties';

export default function validator(
  properties: Record<string, unknown>
): PropertyValidationResult {
  const errors: Array<{ key: string; message: string }> = [];

  // Validate required to phone number
  if (!properties.to || !(properties.to as string).trim()) {
    errors.push({ key: 'to', message: 'Recipient phone number is required' });
  } else {
    const toNumbers = (properties.to as string).split(',').map((n: string) => n.trim());
    for (const number of toNumbers) {
      if (number && !isValidPhoneNumber(number)) {
        errors.push({
          key: 'to',
          message: `Invalid phone number format: ${number}. Use E.164 format (e.g., +44123456789)`,
        });
        break;
      }
    }
  }

  // Validate from phone number if provided (optional)
  if (properties.from && (properties.from as string).trim()) {
    const from = (properties.from as string).trim();
    // Allow alphanumeric sender IDs (max 11 chars) or phone numbers
    if (!isValidPhoneNumber(from) && !isValidSenderId(from)) {
      errors.push({
        key: 'from',
        message: 'From must be a valid phone number (E.164) or alphanumeric sender ID (max 11 characters)',
      });
    }
  }

  // Validate message body
  if (!properties.body || !(properties.body as string).trim()) {
    errors.push({ key: 'body', message: 'Message content is required' });
  } else if ((properties.body as string).length > 1600) {
    errors.push({
      key: 'body',
      message: 'Message exceeds maximum length of 1600 characters',
    });
  }

  // Validate media URLs if MMS is enabled
  if (properties.enableMms && properties.mediaUrls) {
    const mediaUrls = (properties.mediaUrls as string).split(',').map((url: string) => url.trim()).filter(Boolean);
    if (mediaUrls.length > 10) {
      errors.push({
        key: 'mediaUrls',
        message: 'Maximum 10 media URLs allowed',
      });
    }
    for (const url of mediaUrls) {
      if (!isValidUrl(url)) {
        errors.push({
          key: 'mediaUrls',
          message: `Invalid URL format: ${url}`,
        });
        break;
      }
    }
  }

  // Validate validity period
  if (properties.validityPeriod !== undefined) {
    const period = Number(properties.validityPeriod);
    if (isNaN(period) || period < 1 || period > 14400) {
      errors.push({
        key: 'validityPeriod',
        message: 'Validity period must be between 1 and 14400 seconds',
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

function isValidPhoneNumber(phone: string): boolean {
  // Allow expressions like {{ $json.phone }}
  if (phone.includes('{{') && phone.includes('}}')) {
    return true;
  }
  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone.trim());
}

function isValidSenderId(senderId: string): boolean {
  // Allow expressions
  if (senderId.includes('{{') && senderId.includes('}}')) {
    return true;
  }
  // Alphanumeric sender ID: 3-11 characters, letters and numbers only
  const senderIdRegex = /^[a-zA-Z0-9]{3,11}$/;
  return senderIdRegex.test(senderId.trim());
}

function isValidUrl(url: string): boolean {
  // Allow expressions
  if (url.includes('{{') && url.includes('}}')) {
    return true;
  }
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
